// src/components/EventsManager.jsx
import { useEffect, useState, useCallback } from "react";
import {
    Container,
    Typography,
    Box,
    Button,
    TextField,
    Grid,
    Card,
    CardContent,
    IconButton,
    Tooltip,
    Snackbar,
    Alert,
    CircularProgress,
    Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";
import Chip from "@mui/material/Chip";
import { authHeader } from "../auth";

const EMPTY_FORM = { date: "", nom: "", lieu: "", sport: "", site_officiel: "", livetiming: "" };

// 📅 Date du jour au format "YYYY-MM-DD", en heure locale (pas UTC) — pour
// comparer directement aux dates renvoyées par l'API sans parser de Date.
function todayISO() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
        d.getDate()
    ).padStart(2, "0")}`;
}

// 📎 Les 3 types de documents attachables à un évènement, avec leur libellé
// et l'extension attendue par le champ file (voir events.js côté serveur).
const UPLOAD_SLOTS = [
    { type: "image", label: "Image", accept: "image/*" },
    { type: "startlist", label: "Liste de départ", accept: "application/pdf,.doc,.docx" },
    { type: "resultats", label: "Résultats", accept: "application/pdf,.doc,.docx" },
];

export default function EventsManager() {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [newEvent, setNewEvent] = useState(EMPTY_FORM);

    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);

    const [uploadingKey, setUploadingKey] = useState(null); // `${id}:${type}` en cours d'upload

    // 🔍 Recherche + limite d'affichage par défaut aux 3 prochains évènements
    const [search, setSearch] = useState("");
    const [showAll, setShowAll] = useState(false);

    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const showSnackbar = (message, severity = "success") =>
        setSnackbar({ open: true, message, severity });

    const fetchEvents = useCallback(() => {
        setLoading(true);
        fetch("/api/calendar-events")
            .then((res) => res.json())
            .then((data) => setEvents(Array.isArray(data) ? data : []))
            .catch(() => showSnackbar("Erreur lors du chargement des évènements.", "error"))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        fetchEvents();
    }, [fetchEvents]);

    // ➕ Création
    const handleCreate = () => {
        if (!newEvent.date || !newEvent.nom) {
            showSnackbar("Date et nom sont requis.", "error");
            return;
        }
        setCreating(true);
        fetch("/api/calendar-events", {
            method: "POST",
            headers: { "Content-Type": "application/json", ...authHeader() },
            body: JSON.stringify(newEvent),
        })
            .then((res) => {
                if (!res.ok) throw new Error();
                return res.json();
            })
            .then(() => {
                showSnackbar("Évènement créé.");
                setNewEvent(EMPTY_FORM);
                fetchEvents();
            })
            .catch(() => showSnackbar("Échec de la création.", "error"))
            .finally(() => setCreating(false));
    };

    // ✏️ Édition
    const startEdit = (ev) => {
        setEditingId(ev.id);
        setEditForm({
            date: ev.date || "",
            nom: ev.nom || "",
            lieu: ev.lieu || "",
            sport: ev.sport || "",
            site_officiel: ev.site_officiel || "",
            livetiming: ev.livetiming || "",
        });
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditForm(EMPTY_FORM);
    };

    const saveEdit = (id) => {
        if (!editForm.date || !editForm.nom) {
            showSnackbar("Date et nom sont requis.", "error");
            return;
        }
        setSaving(true);
        fetch(`/api/calendar-events/${id}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json", ...authHeader() },
            body: JSON.stringify(editForm),
        })
            .then((res) => {
                if (!res.ok) throw new Error();
                showSnackbar("Évènement mis à jour.");
                cancelEdit();
                fetchEvents();
            })
            .catch(() => showSnackbar("Échec de la mise à jour.", "error"))
            .finally(() => setSaving(false));
    };

    // 🗑️ Suppression de l'évènement entier
    const handleDelete = (ev) => {
        if (!window.confirm(`Supprimer "${ev.nom}" et tous ses documents ? Irréversible.`)) return;

        fetch(`/api/calendar-events/${ev.id}`, { method: "DELETE", headers: authHeader() })
            .then((res) => {
                if (!res.ok) throw new Error();
                showSnackbar("Évènement supprimé.");
                fetchEvents();
            })
            .catch(() => showSnackbar("Échec de la suppression.", "error"));
    };

    // ⬆️ Upload d'un document (image / startlist / résultats)
    const handleUpload = (eventId, type, file) => {
        if (!file) return;
        const key = `${eventId}:${type}`;
        const formData = new FormData();
        formData.append("document", file);

        setUploadingKey(key);
        fetch(`/api/calendar-events/${eventId}/upload/${type}`, {
            method: "POST",
            headers: authHeader(),
            body: formData,
        })
            .then((res) => {
                if (!res.ok) throw new Error();
                showSnackbar("Document ajouté.");
                fetchEvents();
            })
            .catch(() => showSnackbar("Échec de l'upload.", "error"))
            .finally(() => setUploadingKey(null));
    };

    // 🗑️ Suppression d'un document attaché
    const handleRemoveUpload = (eventId, type) => {
        if (!window.confirm("Retirer ce document ?")) return;

        fetch(`/api/calendar-events/${eventId}/upload/${type}`, { method: "DELETE", headers: authHeader() })
            .then((res) => {
                if (!res.ok) throw new Error();
                showSnackbar("Document retiré.");
                fetchEvents();
            })
            .catch(() => showSnackbar("Échec de la suppression.", "error"));
    };

    // 🔍 Recherche : toujours sur TOUT l'historique (sinon impossible de
    // retrouver un vieil évènement à corriger). La limite "3 prochains" ne
    // s'applique que quand on ne cherche rien et que "showAll" est éteint.
    const isSearching = search.trim().length > 0;
    const query = search.trim().toLowerCase();
    const today = todayISO();

    const matchesSearch = (ev) =>
        !isSearching ||
        (ev.nom || "").toLowerCase().includes(query) ||
        (ev.lieu || "").toLowerCase().includes(query) ||
        (ev.sport || "").toLowerCase().includes(query);

    const visibleEvents = events
        .filter(matchesSearch)
        .filter((ev) => isSearching || showAll || ev.date >= today)
        .slice(0, isSearching || showAll ? undefined : 3);

    return (
        <Container maxWidth="md" sx={{ mt: 6, mb: 6 }}>
            <Typography variant="h4" sx={{ mb: 3 }}>
                Gestion du calendrier
            </Typography>

            {/* ➕ Nouvel évènement */}
            <Card sx={{ mb: 4 }}>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Nouvel évènement
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                type="date"
                                label="Date"
                                InputLabelProps={{ shrink: true }}
                                value={newEvent.date}
                                onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={8}>
                            <TextField
                                fullWidth
                                label="Nom"
                                value={newEvent.nom}
                                onChange={(e) => setNewEvent({ ...newEvent, nom: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Lieu"
                                value={newEvent.lieu}
                                onChange={(e) => setNewEvent({ ...newEvent, lieu: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Sport"
                                placeholder="ski alpin, vtt, ..."
                                value={newEvent.sport}
                                onChange={(e) => setNewEvent({ ...newEvent, sport: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Site officiel (URL)"
                                value={newEvent.site_officiel}
                                onChange={(e) =>
                                    setNewEvent({ ...newEvent, site_officiel: e.target.value })
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="LiveTiming (URL)"
                                value={newEvent.livetiming}
                                onChange={(e) =>
                                    setNewEvent({ ...newEvent, livetiming: e.target.value })
                                }
                            />
                        </Grid>
                    </Grid>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        sx={{ mt: 2 }}
                        onClick={handleCreate}
                        disabled={creating}
                    >
                        {creating ? "Création..." : "Créer l'évènement"}
                    </Button>
                </CardContent>
            </Card>

            {/* 🔍 Recherche + compteur + bascule d'affichage */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center", mb: 2 }}>
                <TextField
                    size="small"
                    placeholder="Rechercher (nom, lieu, sport...)"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ flexGrow: 1, minWidth: 240 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                        endAdornment: search && (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setSearch("")}>
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
                {!isSearching && (
                    <Button size="small" onClick={() => setShowAll((v) => !v)}>
                        {showAll ? "Réduire aux 3 prochains" : "Afficher tous les évènements"}
                    </Button>
                )}
            </Box>

            {!loading && events.length > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {visibleEvents.length} affiché{visibleEvents.length > 1 ? "s" : ""} sur{" "}
                    {events.length} au total
                    {!isSearching && !showAll ? " (3 prochains évènements)" : ""}
                </Typography>
            )}

            {/* 📋 Liste des évènements */}
            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress />
                </Box>
            ) : events.length === 0 ? (
                <Typography color="text.secondary">Aucun évènement pour le moment.</Typography>
            ) : visibleEvents.length === 0 ? (
                <Typography color="text.secondary">Aucun évènement ne correspond à la recherche.</Typography>
            ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {visibleEvents.map((ev) => {
                        const isEditing = editingId === ev.id;
                        const isPast = ev.date < today;

                        return (
                            <Card key={ev.id}>
                                <CardContent>
                                    {isEditing ? (
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={4}>
                                                <TextField
                                                    fullWidth
                                                    type="date"
                                                    label="Date"
                                                    InputLabelProps={{ shrink: true }}
                                                    value={editForm.date}
                                                    onChange={(e) =>
                                                        setEditForm({ ...editForm, date: e.target.value })
                                                    }
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={8}>
                                                <TextField
                                                    fullWidth
                                                    label="Nom"
                                                    value={editForm.nom}
                                                    onChange={(e) =>
                                                        setEditForm({ ...editForm, nom: e.target.value })
                                                    }
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Lieu"
                                                    value={editForm.lieu}
                                                    onChange={(e) =>
                                                        setEditForm({ ...editForm, lieu: e.target.value })
                                                    }
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Sport"
                                                    value={editForm.sport}
                                                    onChange={(e) =>
                                                        setEditForm({ ...editForm, sport: e.target.value })
                                                    }
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Site officiel (URL)"
                                                    value={editForm.site_officiel}
                                                    onChange={(e) =>
                                                        setEditForm({
                                                            ...editForm,
                                                            site_officiel: e.target.value,
                                                        })
                                                    }
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    label="LiveTiming (URL)"
                                                    value={editForm.livetiming}
                                                    onChange={(e) =>
                                                        setEditForm({
                                                            ...editForm,
                                                            livetiming: e.target.value,
                                                        })
                                                    }
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Button
                                                    variant="contained"
                                                    startIcon={<SaveIcon />}
                                                    onClick={() => saveEdit(ev.id)}
                                                    disabled={saving}
                                                    sx={{ mr: 1 }}
                                                >
                                                    Enregistrer
                                                </Button>
                                                <Button startIcon={<CloseIcon />} onClick={cancelEdit}>
                                                    Annuler
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    ) : (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "flex-start",
                                            }}
                                        >
                                            <Box>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <Typography variant="h6">{ev.nom}</Typography>
                                                    {isPast && (
                                                        <Chip label="Passé" size="small" variant="outlined" />
                                                    )}
                                                </Box>
                                                <Typography color="text.secondary">
                                                    {ev.date} — {ev.lieu}
                                                    {ev.sport ? ` · ${ev.sport}` : ""}
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Tooltip title="Modifier">
                                                    <IconButton onClick={() => startEdit(ev)}>
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Supprimer">
                                                    <IconButton color="error" onClick={() => handleDelete(ev)}>
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                    )}

                                    <Divider sx={{ my: 2 }} />

                                    {/* 📎 Documents attachés */}
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                                        {UPLOAD_SLOTS.map((slot) => {
                                            const currentUrl = ev[slot.type];
                                            const key = `${ev.id}:${slot.type}`;
                                            const isUploading = uploadingKey === key;

                                            return (
                                                <Box
                                                    key={slot.type}
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 0.5,
                                                        border: "1px solid #eee",
                                                        borderRadius: 1,
                                                        px: 1,
                                                        py: 0.5,
                                                    }}
                                                >
                                                    <Typography variant="body2" sx={{ mr: 0.5 }}>
                                                        {slot.label} :
                                                    </Typography>

                                                    {currentUrl ? (
                                                        <>
                                                            <a
                                                                href={currentUrl}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                style={{ fontSize: "0.875rem" }}
                                                            >
                                                                voir
                                                            </a>
                                                            <Tooltip title="Retirer">
                                                                <IconButton
                                                                    size="small"
                                                                    color="error"
                                                                    onClick={() =>
                                                                        handleRemoveUpload(ev.id, slot.type)
                                                                    }
                                                                >
                                                                    <CloseIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </>
                                                    ) : (
                                                        <Button
                                                            size="small"
                                                            component="label"
                                                            startIcon={<UploadFileIcon fontSize="small" />}
                                                            disabled={isUploading}
                                                        >
                                                            {isUploading ? "Envoi..." : "Ajouter"}
                                                            <input
                                                                type="file"
                                                                hidden
                                                                accept={slot.accept}
                                                                onChange={(e) =>
                                                                    handleUpload(
                                                                        ev.id,
                                                                        slot.type,
                                                                        e.target.files?.[0]
                                                                    )
                                                                }
                                                            />
                                                        </Button>
                                                    )}
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </CardContent>
                            </Card>
                        );
                    })}
                </Box>
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
            >
                <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
            </Snackbar>
        </Container>
    );
}